import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  get_active_clients_list,
  send_affiliation_request,
} from "../../../DAL/clientTypes/ClientTpes";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import CircularLoader from "../../../components/loader/CircularLoader";
import RecordNoImage from "../../../components/recordNotFound/RecordNoImage";
import ClientsCard from "../../clientTypes/components/ClientsCard";
import { useIds } from "../../../hook/AdminContext";
import CompanyPopup from "../../../components/popUp/CompanyPopup";
import { CircularProgress } from "@mui/material";
import ClientHoverImageCard from "../../clientTypes/components/ClientHoverImageCard";
import AffiliateCard from "../../clientTypes/components/AffiliateCard";

export default function AddAffiliate({ setIsChanged }) {
  const { userInfo } = useIds();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [clientsList, setClientsList] = useState([]);
  const [targetClient, setTargetClient] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);

  // load more states and function

  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observer = useRef();

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && totalPages > pageNumber) {
          setPageNumber(pageNumber + 1);
          setIsLoadingMore(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totalPages, pageNumber, observer]
  );

  const handleGetClients = async () => {
    const resp = await get_active_clients_list(search, pageNumber);
    if (resp.code == 200) {
      if (isLoadingMore) {
        let newArray = clientsList.concat(resp?.data?.clients || []);
        setClientsList(newArray);
      } else {
        setClientsList(resp?.data?.clients);
      }
      setTotalPages(resp?.data?.total_page);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setLoading(false);
    setIsLoadingMore(false);
  };

  const handleSendAffiliationRequest = async (obj) => {
    // Set loading state for this client
    setClientsList((prev) =>
      prev.map((client) =>
        client._id === obj._id ? { ...client, loading: true } : client
      )
    );

    const postData = {
      receiver: obj._id,
      sender: userInfo.profile_id,
    };

    const resp = await send_affiliation_request(postData);

    if (resp.code === 200) {
      enqueueSnackbar(resp.message, { variant: "success" });
      // Set request_sended true and remove loading
      setClientsList((prev) =>
        prev.map((client) =>
          client._id === obj._id
            ? { ...client, loading: false, request_sended: true }
            : client
        )
      );
      setIsChanged((prev) => !prev);
    } else {
      // Remove loading on error
      enqueueSnackbar(resp.message, { variant: "error" });
      setClientsList((prev) =>
        prev.map((client) =>
          client._id === obj._id ? { ...client, loading: false } : client
        )
      );
    }
  };

  useEffect(() => {
    handleGetClients();
  }, [pageNumber]);

  return (
    <>
      {openDetails && (
        <CompanyPopup
          companyModalOpen={openDetails}
          handleCloseCompanyModal={() => setOpenDetails(false)}
          companyModalData={targetClient}
        />
      )}

      <div className="add-affiliate-modal">
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="search-wrapper">
              <form
                className="search-box"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (pageNumber == 0) {
                    setLoading(true);
                    handleGetClients();
                  } else {
                    setLoading(true);
                    setPageNumber(0);
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Search clients for affiliation..."
                  className="search-input"
                  search={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />

                <button className="search-btn" type="submit">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    className="search-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
        {loading ? (
          <CircularLoader />
        ) : (
          <>
            {clientsList.length > 0 ? (
              <div className="d-grid gap-3 grid-template">
                {clientsList.map((item) => (
                  <AffiliateCard
                    client={item}
                    handleNavigateDetails={(item) => {
                      setTargetClient(item);
                      setOpenDetails(true);
                    }}
                    showAdditionalButton
                    additionButton={
                      <div className="d-flex justify-content-center pt-2">
                        <button
                          className="btn-contained-sm ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendAffiliationRequest(item);
                          }}
                          disabled={item.loading || item.request_sended}
                        >
                          {item.request_sended
                            ? "Request Sent"
                            : item.loading
                            ? "Sending...."
                            : "Send Request"}
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="mt-5">
                <RecordNoImage title={"Clients Not Found"} />
              </div>
            )}

            {!loading &&
              totalPages > 1 &&
              totalPages > pageNumber &&
              clientsList.length > 0 && (
                <div className="col-12 text-center mt-3">
                  <div
                    ref={lastBookElementRef}
                    className="mt-3"
                    onClick={() => {
                      setPageNumber(pageNumber + 1);
                    }}
                    id="load-more-feed"
                  >
                    {isLoadingMore ? (
                      <CircularProgress size={15} />
                    ) : (
                      "Load More"
                    )}
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    </>
  );
}
